"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

// Fade In animation component
export const FadeIn = ({ 
  children, 
  delay = 0, 
  duration = 0.5,
  className,
  ...props 
}: { 
  children: React.ReactNode; 
  delay?: number;
  duration?: number;
  className?: string;
  [key: string]: any;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration, delay }}
      className={cn(className)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Slide Up animation component
export const SlideUp = ({ 
  children, 
  delay = 0, 
  duration = 0.5,
  distance = 20,
  className,
  ...props 
}: { 
  children: React.ReactNode; 
  delay?: number;
  duration?: number;
  distance?: number;
  className?: string;
  [key: string]: any;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: distance }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: distance }}
      transition={{ duration, delay }}
      className={cn(className)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Staggered animation for lists
export const StaggeredList = ({ 
  children, 
  delay = 0.1,
  staggerDelay = 0.1,
  className,
  ...props 
}: { 
  children: React.ReactNode; 
  delay?: number;
  staggerDelay?: number;
  className?: string;
  [key: string]: any;
}) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: { staggerChildren: staggerDelay, delayChildren: delay }
        },
        hidden: {}
      }}
      className={cn(className)}
      {...props}
    >
      {React.Children.map(children, child => (
        <motion.div
          variants={{
            visible: { opacity: 1, y: 0 },
            hidden: { opacity: 0, y: 20 }
          }}
          transition={{ duration: 0.5 }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Page transition wrapper
export const PageTransition = ({ children }: { children: React.ReactNode }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-grow"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Loading spinner
export const Spinner = ({ size = 'default', className, ...props }: { 
  size?: 'sm' | 'default' | 'lg'; 
  className?: string;
  [key: string]: any;
}) => {
  const sizeClasses = {
    sm: "h-4 w-4 border-2",
    default: "h-6 w-6 border-2",
    lg: "h-8 w-8 border-3",
  };
  
  return (
    <div 
      className={cn(
        "rounded-full animate-spin border-gray-300 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-400", 
        sizeClasses[size],
        className
      )}
      {...props}
    />
  );
};